import React from 'react';
import { FaChild, FaBaby, FaWheelchair, FaUserNurse, FaSchool, FaArrowRight, FaCheckCircle, FaEnvelope, FaStar } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import { Link } from "react-router-dom";
import { useColorMode } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import image1 from '../../../Asset/image13.jpg';
import ImageA from '../../../Asset/image11.jpg';
// L'import de QuickCallWidget a été supprimé ici !

const GardeEnfants = () => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const sectionVariants = { 
    hidden: { opacity: 0, y: 30 }, 
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } 
  };

  return (
    <div className={`min-h-screen overflow-hidden transition-colors duration-300 ${isDark ? 'bg-[#0a0f16]' : 'bg-[#FAFCFF]'}`}>
      <Helmet>
        <title>Garde d'enfants à domicile — Hetep Iaout Services</title>
        <meta name="description" content="Garde d'enfants à domicile certifiée en Île-de-France. Intervenants qualifiés pour la petite enfance, aide aux devoirs et activités périscolaires. Devis gratuit." />
        <link rel="canonical" href="https://www.hisvie.com/enfants/" />
      </Helmet>

      {/* --- HERO SECTION (ULTRA PREMIUM & IMMERSIF) --- */}
      <div className="relative w-full min-h-[100vh] flex items-center pt-20 overflow-hidden">
          
          {/* 1. Image de fond avec effet de zoom subtil au chargement */}
          <motion.img 
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              transition={{ duration: 10, ease: "easeOut" }}
              src={image1} 
              alt="Garde d'enfants" 
              className="absolute inset-0 w-full h-full object-cover z-0 origin-center" 
          />
          
          {/* 2. Overlay sombre (Mobile) / Dégradé (PC) */}
          <div className="absolute inset-0 bg-black/60 md:bg-black/0 md:bg-gradient-to-r md:from-[#0a0f16]/90 md:via-[#0a0f16]/60 md:to-black/40 z-10"></div>

          {/* 3. Le contenu (Texte + Boutons) */}
          <div className="relative z-20 w-full max-w-7xl mx-auto px-4 md:px-6 flex flex-col lg:flex-row items-center justify-between">
              
              {/* --- Colonne Gauche : Texte --- */}
              <motion.div 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 0.2 }}
                  className="w-full lg:w-2/3 mt-10 lg:mt-0"
              >
                  {/* Badge de réassurance */}
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-md mb-6 md:mb-8 shadow-lg">
                      <FaStar className="text-yellow-400 text-xs md:text-sm" />
                      <span className="text-white text-[10px] md:text-xs font-bold tracking-widest uppercase">Garde d'Enfants</span>
                  </div>

                  <h1 className="text-4xl sm:text-5xl md:text-7xl font-black leading-[1.1] tracking-tight mb-6 text-white">
                      Grandir <br className="hidden md:block"/>
                      <span className="text-yellow-400 font-light italic font-serif">ensemble,</span> <br/>
                      en toute confiance.
                  </h1>

                  <p className="text-base md:text-xl mb-10 max-w-lg leading-relaxed text-gray-300">
                      Parce que chaque instant de leur enfance est précieux, nous avons conçu des solutions de garde qui privilégient leur épanouissement et votre tranquillité d'esprit.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4">
                      <Link to="/demande-de-devis" className="w-full sm:w-auto">
                          <motion.button 
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              className="w-full bg-blue-600 text-white font-bold py-4 px-8 rounded-2xl shadow-xl shadow-blue-600/30 hover:bg-blue-700 transition-colors flex items-center justify-center gap-3 text-base md:text-lg"
                          >
                              Évaluer mes besoins <FaArrowRight size={14} />
                          </motion.button>
                      </Link>
                      <Link to="/contact" className="w-full sm:w-auto">
                          <motion.button 
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              className="w-full font-bold py-4 px-8 rounded-2xl transition-all border flex items-center justify-center gap-3 text-base md:text-lg bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-md"
                          >
                              En parler avec un conseiller
                          </motion.button>
                      </Link>
                  </div>
              </motion.div>

              {/* --- Colonne Droite : Cartes flottantes (PC) --- */}
              <motion.div 
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 1, delay: 0.6 }}
                  className="hidden lg:flex w-2/5 flex-col items-end justify-center gap-6 mt-20"
              >
                  {/* Carte Agréé SAP (Glassmorphism) - Seule carte */}
                  <div className="p-6 rounded-[2rem] shadow-2xl border backdrop-blur-xl max-w-sm w-full bg-white/10 border-white/20">
                      <div className="flex items-start gap-4">
                          <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white shrink-0 shadow-inner">
                              <FaCheckCircle size={24} />
                          </div>
                          <div>
                              <p className="font-black text-xl mb-1 text-white">Agréé SAP</p>
                              <p className="text-sm leading-relaxed text-gray-200">
                                  19 ans d'expertise au service des familles. Crédit d'impôt immédiat disponible.
                              </p>
                          </div>
                      </div>
                  </div>
              </motion.div>
          </div>

          {/* --- Indicateur de Scroll --- */}
          <motion.div 
              animate={{ y: [0, 10, 0] }}
              transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
              className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 hidden md:flex flex-col items-center gap-3 opacity-60"
          >
              <span className="text-[10px] font-bold tracking-widest uppercase text-white">Découvrir</span>
              <div className="w-[2px] h-12 rounded-full bg-gradient-to-b from-white to-transparent"></div>
          </motion.div>
      </div>

     {/* --- BENTO GRID SERVICES : ÉVEIL, PROTECTION & CONFIANCE --- */}
      <motion.section 
        initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={sectionVariants}
        className="py-12 md:py-24 px-4 md:px-6 max-w-7xl mx-auto"
      >
        <div className="text-left mb-10 md:mb-16 max-w-3xl">
            <div className="flex items-center gap-3 mb-4 text-blue-600 font-bold uppercase tracking-widest text-xs">
                <span className="w-8 h-px bg-blue-600"></span>
                Nos piliers d'accompagnement
            </div>
            <h2 className={`text-3xl md:text-6xl font-black mb-4 leading-tight ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                Une attention <span className="text-blue-600 font-serif italic font-light">singulière</span> pour chaque âge.
            </h2>
            <p className={`text-base md:text-xl ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Parce qu'on ne garde pas un nourrisson comme on accompagne un adolescent, nos protocoles sont segmentés par étapes de vie.
            </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 md:grid-rows-2 gap-4 md:gap-6">
            
            {/* Carte 1 : Plus de 3 ans (LA CARTE ANCRE) */}
            <div className={`md:col-span-4 md:row-span-1 rounded-[2.5rem] p-8 md:p-12 relative overflow-hidden group border transition-all duration-500 shadow-sm hover:shadow-2xl ${isDark ? 'bg-gray-800/80 border-gray-700' : 'bg-[#F2F7FF] border-blue-100'}`}>
                <div className="relative z-10 flex flex-col md:flex-row h-full items-center gap-8">
                    <div className="flex-1 text-left">
                        <div className="w-14 h-14 bg-white dark:bg-gray-900 rounded-2xl flex items-center justify-center mb-6 shadow-sm text-blue-600">
                            <FaChild size={28} />
                        </div>
                        <h3 className={`text-2xl md:text-4xl font-black mb-4 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Plus de 3 ans</h3>
                        <p className={`text-sm md:text-lg leading-relaxed mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                            Bien plus qu'une présence : une aide aux devoirs, la préparation de repas équilibrés et un éveil culturel pour un retour d'école stimulant.
                        </p>
                        <div className="flex items-center gap-4">
                            <span className={`text-xs font-bold px-3 py-1 rounded-full ${isDark ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>Autonomie</span>
                            <span className={`text-xs font-bold px-3 py-1 rounded-full ${isDark ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>Éveil social</span>
                        </div>
                    </div>
                    <div className="hidden lg:block w-1/3 opacity-20 group-hover:opacity-40 transition-opacity duration-700">
                        <FaSchool size={180} className="text-blue-600 rotate-12" />
                    </div>
                </div>
            </div>

            {/* Carte 2 : Moins de 3 ans (CONFIANCE ABSOLUE) */}
            <div className={`md:col-span-2 md:row-span-1 rounded-[2.5rem] p-8 flex flex-col justify-between shadow-xl transition-all duration-500 border ${isDark ? 'bg-blue-900 border-blue-800' : 'bg-[#1B365D] border-[#1B365D]'}`}>
                <div className="relative z-10">
                    <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mb-6 backdrop-blur-md text-yellow-400">
                        <FaBaby size={28} />
                    </div>
                    <h3 className="text-2xl md:text-3xl font-black text-white mb-4">Petite Enfance</h3>
                    <p className="text-sm md:text-base text-blue-100/80 font-light leading-relaxed mb-6">
                        Spécialistes du nourrisson : respect scrupuleux du cycle de sommeil, hygiène médicale et bienveillance absolue.
                    </p>
                </div>
                <div className="flex items-center gap-2 text-yellow-400 font-bold text-xs">
                    <FaCheckCircle /> Intervenants diplômés d'État
                </div>
            </div>

            {/* Carte 3 : Handicap (EXPERTISE & DOUCEUR) */}
            <div className={`md:col-span-2 md:row-span-1 rounded-[2.5rem] p-8 flex flex-col shadow-sm border transition-all duration-500 ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-100 hover:border-green-200 hover:shadow-xl'}`}>
                <div className="w-14 h-14 bg-green-50 dark:bg-green-900/30 rounded-2xl flex items-center justify-center mb-6 text-green-600">
                    <FaWheelchair size={28} />
                </div>
                <div className="text-left">
                    <h3 className={`text-2xl font-black mb-3 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Handicap</h3>
                    <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Protocoles de soins spécifiques et accompagnement spécialisé pour favoriser l'épanouissement malgré la singularité.
                    </p>
                </div>
            </div>

            {/* Carte 4 : Le Relais Parental (LA SOLUTION FLEXIBLE) */}
            <div className={`md:col-span-4 md:row-span-1 rounded-[2.5rem] p-8 md:p-12 relative overflow-hidden group transition-all duration-500 border ${isDark ? 'bg-gray-800/80 border-gray-700' : 'bg-[#FFFDF2] border-yellow-100/50'}`}>
                <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-400/10 rounded-full blur-[100px] pointer-events-none group-hover:scale-125 transition-transform duration-1000"></div>
                
                <div className="relative z-10 flex flex-col md:flex-row gap-8 items-center h-full">
                    <div className="w-20 h-20 bg-white shadow-xl rounded-[2rem] flex items-center justify-center text-yellow-600 shrink-0">
                        <FaUserNurse size={40} />
                    </div>
                    <div className="text-left">
                        <h3 className={`text-2xl md:text-3xl font-black mb-2 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Relais & Babysitting</h3>
                        <p className={`text-sm md:text-lg leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                            Impondérables, sorties de crèche ou besoin ponctuel : nos équipes assurent la continuité de votre organisation familiale 24h/7j.
                        </p>
                    </div>
                    <div className="shrink-0 flex items-center justify-center">
                         <div className={`p-4 rounded-2xl border-2 border-dashed ${isDark ? 'border-gray-600' : 'border-yellow-200'}`}>
                            <span className="text-xl md:text-2xl font-black text-yellow-600">Dispo 24/7</span>
                         </div>
                    </div>
                </div>
            </div>
        </div>
      </motion.section>

      {/* --- SECTION SOLUTION SÉRÉNITÉ : SPOTLIGHT --- */}
      <motion.section
        initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={sectionVariants}
        className="max-w-7xl mx-auto px-4 md:px-6 pb-20 md:pb-32"
      >
        <div className={`relative overflow-hidden rounded-[3rem] shadow-2xl p-8 md:p-16 flex flex-col lg:flex-row items-center gap-12 border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-[#1B365D]'}`}>
          
          <div className="absolute top-0 right-0 w-full h-full bg-blue-500/10 blur-[120px] pointer-events-none transform translate-x-1/4"></div>

          {/* Image avec cadre "Polaroid Premium" */}
          <div className="w-full lg:w-1/2 h-[300px] md:h-[450px] rounded-[2.5rem] overflow-hidden shadow-2xl border-8 border-white/10 relative">
            <img src={ImageA} alt="Moment de complicité" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1B365D]/40 to-transparent"></div>
          </div>

          <div className="w-full lg:w-1/2 relative z-10 text-left">
            <div className="w-12 h-1.5 bg-yellow-400 rounded-full mb-6"></div>
            <h2 className="text-3xl md:text-5xl font-black text-white mb-6 leading-[1.1]">
                Un quotidien <span className="text-yellow-400 italic font-serif font-light">libéré.</span>
            </h2>
            <p className="text-blue-100/80 text-sm md:text-lg leading-relaxed mb-8">
                Nos intervenants prennent tout en charge : du trajet école-maison à la gestion du linge, en passant par la préparation de repas équilibrés. Concentrez-vous sur vos moments précieux, nous gérons le reste.
            </p>
            <div className="flex flex-wrap gap-4 text-yellow-400 font-bold text-sm">
                <div className="flex items-center gap-2"><FaCheckCircle size={14}/> Crédit d'impôt immédiat</div>
                <div className="flex items-center gap-2"><FaCheckCircle size={14}/> Flexibilité totale</div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* --- CTA FINAL : CONVERSATION --- */}
      <motion.section
        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={sectionVariants}
        className="max-w-5xl mx-auto px-4 pb-24"
      >
        <div className={`relative overflow-hidden rounded-[2.5rem] p-10 md:p-16 flex flex-col md:flex-row items-center gap-10 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white shadow-2xl shadow-blue-900/5 border-blue-50'}`}>
          <div className="flex-1 z-10 text-center md:text-left">
            <h2 className={`text-3xl md:text-4xl font-black mb-4 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
              Prêt à offrir le <span className="text-blue-600">meilleur</span> à votre enfant ?
            </h2>
            <p className={`text-base md:text-lg ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Discutons ensemble de la solution de garde qui correspond à votre rythme de vie.</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto z-10">
            <Link to="/contact" className="w-full sm:w-auto">
                <button className="flex items-center justify-center gap-3 bg-blue-600 text-white px-8 py-4 rounded-2xl font-bold hover:bg-blue-700 transition-all w-full shadow-lg shadow-blue-600/20">
                    Nous contacter
                </button>
            </Link>
            <a href="mailto:his@donnerlavieauxannees.com" className="flex items-center justify-center gap-3 bg-yellow-50 text-yellow-700 px-8 py-4 rounded-2xl font-bold hover:bg-yellow-100 transition-all border border-yellow-100">
                <FaEnvelope /> Email
            </a>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default GardeEnfants;